// Block K (UI-20): every word and colour the Admin panel shows for a stored value, in one file
// (VSC-BLOCKS UI-20: "human labels from one mapping file"). Pure functions, unit tested in
// AdminPanel.test.js. Colours are token names (src/ui/tokens), never hex.

// The activity log's action types. Anything the server writes that is not listed here still
// shows, under its own name, so a new action is never hidden.
export const ACTION_LOOK = {
  account_change: { label: 'Account change', color: 'pastel-blue' },
  trial_extension_request: { label: 'Trial extension asked', color: 'pastel-yellow' },
  trial_extension_approved: { label: 'Trial extended', color: 'pastel-green' },
  trial_extension_rejected: { label: 'Extension declined', color: 'pastel-coral' },
  user_created: { label: 'Account created', color: 'pastel-periwinkle' },
  user_removed: { label: 'Account removed', color: 'pastel-coral' },
  password_reset_sent: { label: 'Password reset', color: 'pastel-lilac' },
};
export function actionLook(action) {
  return ACTION_LOOK[action] || { label: String(action || 'Unknown').replace(/_/g, ' '), color: 'pastel-neutral' };
}

// Pills: the four tones the kit has (success, pending, warning, neutral).
export function planTone(plan) {
  const key = String(plan || '').split('+')[0].trim().toLowerCase();
  if (['pro', 'team', 'enterprise', 'basics', 'personal', 'family'].includes(key)) return 'success';
  if (key === 'trial' || key === '') return 'pending';
  return 'neutral';
}
export function statusTone(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'active') return 'success';
  if (s === 'trialing' || s === 'pending') return 'pending';
  if (s === 'suspended' || s === 'closed' || s === 'rejected' || s === 'failed') return 'warning';
  return 'neutral';
}
export function logStatusTone(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'completed' || s === 'approved') return 'success';
  if (s === 'pending') return 'pending';
  if (s === 'rejected' || s === 'failed') return 'warning';
  return 'neutral';
}

const ROLE_LABEL = { super_admin: 'Super admin', cs_manager: 'CS manager', cs_agent: 'CS agent', admin: 'Admin', member: 'Member', user: 'Member' };
export const roleLabel = (role) => ROLE_LABEL[String(role || '').toLowerCase()] || (role ? String(role).replace(/_/g, ' ') : 'Unknown');

const USER_TYPE_LABEL = {
  influencer: 'Influencer', friends_family: 'Friends and family', pro_team_owner: 'Team owner',
  pro_team_member: 'Team member', pro_user: 'Pro', basic_user: 'Basic',
};
export const userTypeLabel = (t) => USER_TYPE_LABEL[String(t || '').toLowerCase()] || (t ? String(t).replace(/_/g, ' ') : '');

// ACCOUNT-3: the account's real commercial terms, from its active programme (falling back to the
// subscription status). `fmt` formats a date (the page passes formatDate).
export function programmeTerms(u, fmt = (d) => d) {
  const p = u?.programme;
  if (p) {
    const ends = p.endsAt ? fmt(p.endsAt) : null;
    switch (p.type) {
      case 'staff': return 'Staff';
      case 'reviewer': return 'Reviewer';
      case 'comp_open': return 'Comp, no expiry';
      case 'comp_fixed': return `Comp until ${ends || 'no date set'}`;
      case 'trial': return `Trial ends ${ends || 'on no date set'}`;
      case 'discount': return `${p.discountPercent ?? 0}% discount`;
      default: return String(p.type);
    }
  }
  const st = String(u?.subscriptionStatus || '').toLowerCase();
  if (st === 'active') return 'Paid, active';
  if (st === 'trialing') return 'Stripe trial';
  if (st === 'past_due') return 'Payment due';
  if (st === 'cancelled' || st === 'canceled') return 'Cancelled';
  if (st === 'cancelled_payment_failed') return 'Cancelled, payment failed';
  if (st === 'unpaid') return 'Unpaid, access paused';
  if (st === 'incomplete') return 'Payment incomplete';
  if (st === 'incomplete_expired') return 'Never started';
  if (st === 'paused') return 'Paused';
  return 'No terms';
}

const PROGRAMME_LABEL = { staff: 'Staff', reviewer: 'Reviewer', comp_open: 'Comp, no expiry', comp_fixed: 'Comp until a date', trial: 'Trial', discount: 'Discount' };
export const programmeLabel = (t) => PROGRAMME_LABEL[t] || String(t || '');

// SEAT-5 ownership transfer. What each job state means (shown under the job).
export const TRANSFER_STATE = {
  pending: { label: 'Starting', tone: 'pending', help: 'The checks passed and the new subscription is being created.' },
  awaiting_payment: { label: 'Waiting for a card', tone: 'pending', help: 'The new subscription exists but is not active yet. The new owner must add a card. Resume once they have paid.' },
  flipped: { label: 'Moved, old plan to cancel', tone: 'pending', help: 'Ownership has moved and the new owner is active. Cancelling the old subscription is still to do. Resume to retry it.' },
  completed: { label: 'Completed', tone: 'success', help: 'Transfer complete. The old subscription is cancelled.' },
  failed: { label: 'Failed', tone: 'warning', help: 'Something went wrong; the message says what. Resume is safe to retry.' },
  aborted: { label: 'Cancelled', tone: 'neutral', help: 'Stopped before ownership moved. Nothing changed.' },
};
export const transferState = (s) => TRANSFER_STATE[s] || { label: String(s || 'Unknown'), tone: 'neutral', help: '' };

// The safety checks, in the order lib/ownershipTransfer.js precheckTransfer runs them. It stops
// at the first failure, so the checks before it passed and the ones after it were not reached.
export const TRANSFER_CHECKS = [
  { key: 'business', label: 'The business exists and has an owner', reasons: ['business_not_found', 'business_has_no_owner'] },
  { key: 'not-owner', label: 'The new owner is not already the owner', reasons: ['target_is_already_owner'] },
  { key: 'owner-active', label: 'The current owner is active or trialing', reasons: ['owner_not_found', 'owner_not_active'] },
  { key: 'member', label: 'The new owner is a member of the business', reasons: ['target_not_a_member'] },
  { key: 'household', label: 'The current owner has no household with dependent logins', reasons: ['owner_has_dependent_household'] },
  { key: 'pending', label: 'No transfer is already in progress', reasons: ['transfer_already_pending'] },
];
const REASON_TEXT = {
  business_not_found: 'No business has that ID.',
  business_has_no_owner: 'That business has no owner on record.',
  target_is_already_owner: 'That person already owns the business.',
  owner_not_found: 'The current owner\'s account was not found.',
  owner_not_active: 'The current owner is not active or trialing.',
  target_not_a_member: 'That person is not a member of the business.',
  owner_has_dependent_household: 'The current owner also owns a household with dependent logins.',
  transfer_already_pending: 'A transfer for this business is already in progress.',
};
export const reasonText = (reason) => REASON_TEXT[reason] || (reason ? String(reason).replace(/_/g, ' ') : 'The server refused without a reason.');

// result: the precheck answer ({ ok } or { ok:false, reason, detail }). Returns each check with
// state 'passed' | 'failed' | 'not-reached'. A reason no check knows is added as its own failure.
export function checkStates(result) {
  if (!result) return [];
  if (result.ok) return TRANSFER_CHECKS.map((c) => ({ ...c, state: 'passed' }));
  const at = TRANSFER_CHECKS.findIndex((c) => c.reasons.includes(result.reason));
  if (at === -1) {
    return [...TRANSFER_CHECKS.map((c) => ({ ...c, state: 'not-reached' })), { key: 'other', label: reasonText(result.reason), state: 'failed', detail: result.detail }];
  }
  return TRANSFER_CHECKS.map((c, i) => ({ ...c, state: i < at ? 'passed' : i === at ? 'failed' : 'not-reached', detail: i === at ? result.detail : undefined }));
}

// A refusal or failure the transfer routes sent. They answer 409 with { ok:false, reason, detail }
// and api() throws on a 409, so the reason is on the error's body, not its message (which only
// reads "Request failed with status 409").
export function transferFailure(err, fallback) {
  const b = err?.body;
  if (b && (b.reason || b.detail)) return [reasonText(b.reason), b.detail].filter(Boolean).join(' ');
  if (b?.error) return b.error;
  return err?.message || fallback;
}

// The activity log grouped by the local day, newest first. `dayLabel` formats a heading.
export function groupByDay(logs, dayLabel) {
  const groups = [];
  const byKey = new Map();
  for (const log of logs) {
    const d = new Date(log.createdAt);
    const key = Number.isNaN(d.getTime()) ? 'unknown' : `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    if (!byKey.has(key)) {
      const g = { key, label: key === 'unknown' ? 'No date' : dayLabel(d), items: [] };
      byKey.set(key, g);
      groups.push(g);
    }
    byKey.get(key).items.push(log);
  }
  return groups;
}
